import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { auth } from '../firebaseConfig';
import {
  Dialog,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  Alert
} from '@mui/material';

const Login = ({ isModal = false, onSuccess, onClose }) => {
  const navigate = useNavigate();
  
  // Use refs to manage input values directly
  const emailRef = useRef('');
  const passwordRef = useRef('');
  
  // State for managing UI and errors
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  
  // Refs for input elements
  const emailInputRef = useRef(null);
  const passwordInputRef = useRef(null);

  // Focus email input when modal opens
  useEffect(() => {
    if (isModal) {
      // Slight delay to ensure DOM is ready
      const timer = setTimeout(() => {
        emailInputRef.current?.focus();
      }, 100);
      
      return () => clearTimeout(timer);
    }
  }, [isModal]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Use current values from refs
      await signInWithEmailAndPassword(
        auth, 
        emailRef.current, 
        passwordRef.current
      );
      
      // Reset loading state
      setLoading(false);

      if (isModal) {
        onSuccess?.();
      } else {
        navigate('/projects');
      }
    } catch (err) {
      // Set error and reset loading
      setLoading(false);
      setError(
        err.code === 'auth/invalid-credential' 
          ? 'Invalid email or password' 
          : 'An unexpected error occurred'
      );

      console.error('Login Error', {
        code: err.code,
        message: err.message
      });
    }
  };

  // Controlled input with ref update
  const handleEmailChange = (e) => {
    emailRef.current = e.target.value;
  };

  // Controlled input with ref update
  const handlePasswordChange = (e) => {
    passwordRef.current = e.target.value;
  };

  // Render login form
  const LoginContent = () => (
    <form onSubmit={handleSubmit} style={{ width: '100%' }}>
      <TextField
        inputRef={emailInputRef}
        margin="normal"
        required
        fullWidth
        label="Email Address"
        type="email"
        autoComplete="email"
        onChange={handleEmailChange}
        autoFocus={isModal}
      />
      
      <TextField
        inputRef={passwordInputRef}
        margin="normal"
        required
        fullWidth
        label="Password"
        type="password"
        autoComplete="current-password"
        onChange={handlePasswordChange}
      />
      
      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}
      
      <Button
        type="submit"
        fullWidth
        variant="contained"
        disabled={loading}
        sx={{ mt: 3, mb: 2 }}
      >
        {loading ? 'Signing in...' : 'Sign In'}
      </Button>
    </form>
  );

  // Modal version
  if (isModal) {
    return (
      <Dialog 
        open={true} 
        onClose={onClose} 
        maxWidth="xs" 
        fullWidth
        disableEscapeKeyDown={loading}
      >
        <DialogContent>
          <Typography 
            component="h1" 
            variant="h5" 
            align="center" 
            sx={{ mb: 2 }}
          >
            Sign In
          </Typography>
          
          <LoginContent />
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={onClose} 
            color="primary" 
            disabled={loading}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  // Full page version
  return (
    <div style={{ 
      display: 'flex', 
      flexDirection: 'column', 
      alignItems: 'center', 
      marginTop: '64px' 
    }}>
      <Typography 
        component="h1" 
        variant="h5" 
        sx={{ mb: 2 }}
      >
        Sign In
      </Typography>
      
      <LoginContent />
    </div>
  );
};

export default Login;